'use client';

import { c as _c } from "react/compiler-runtime";
import { useState, useEffect, useRef, useCallback } from 'react';
import { useRouter } from 'next/navigation';
import Link from 'next/link';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSpinnerThird } from '@awesome.me/kit-94e593bf23/icons/classic/light';
import { useReCaptcha } from 'next-recaptcha-v3';
import validatePasswordResetCodeAction from '@/app/_lib/actions/ValidatePasswordResetCodeAction';
import sendPasswordResetCodeAction from '@/app/_lib/actions/SendPasswordResetCodeAction';
import { getResetEmail, getMaskedEmail, setResetEmail, setCustomerCode } from './resetPasswordStorage';

/**
 * CodeStep - Step 2 of password reset flow
 *
 * User enters the 6-digit code sent to their email.
 * On success, redirects to /complete step.
 *
 * If user arrives without email in storage (e.g., direct navigation
 * or "Already have a code?" link), shows email field too.
 */
const CodeStep = () => {
  const $ = _c(56);
  const router = useRouter();
  const {
    executeRecaptcha
  } = useReCaptcha();
  const [email, setEmail] = useState("");
  const [maskedEmail, setMaskedEmailState] = useState("");
  const [needsEmail, setNeedsEmail] = useState(false);
  const [isInitialized, setIsInitialized] = useState(false);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = ["", "", "", "", "", ""];
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const [codeDigits, setCodeDigits] = useState(t0);
  const [isPending, setIsPending] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  let t1;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = [];
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const codeInputRefs = useRef(t1);
  let t2;
  let t3;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = () => {
      const storedEmail = getResetEmail();
      const storedMaskedEmail = getMaskedEmail();
      if (storedEmail) {
        setEmail(storedEmail);
        setMaskedEmailState(storedMaskedEmail || storedEmail);
        setNeedsEmail(false);
      } else {
        setNeedsEmail(true);
      }
      setIsInitialized(true);
    };
    t3 = [];
    $[2] = t2;
    $[3] = t3;
  } else {
    t2 = $[2];
    t3 = $[3];
  }
  useEffect(t2, t3);
  let t4;
  let t5;
  if ($[4] !== isInitialized || $[5] !== needsEmail) {
    t4 = () => {
      if (isInitialized && !needsEmail && codeInputRefs.current[0]) {
        codeInputRefs.current[0].focus();
      }
    };
    t5 = [isInitialized, needsEmail];
    $[4] = isInitialized;
    $[5] = needsEmail;
    $[6] = t4;
    $[7] = t5;
  } else {
    t4 = $[6];
    t5 = $[7];
  }
  useEffect(t4, t5);
  let t6;
  if ($[8] !== codeDigits || $[9] !== email || $[10] !== router) {
    t6 = async e => {
      e.preventDefault();
      if (!email) {
        setErrorMessage("Please enter your email address.");
        return;
      }
      setIsPending(true);
      setErrorMessage("");
      const code = codeDigits.join("");
      const response = await validatePasswordResetCodeAction({
        email,
        code
      });
      if (response.success) {
        setCustomerCode(response.customerCode);
        router.push("/account/reset-password/complete");
      } else {
        setErrorMessage(response.message || "Invalid code. Please try again.");
        setIsPending(false);
      }
    };
    $[8] = codeDigits;
    $[9] = email;
    $[10] = router;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  const handleCodeSubmit = t6;
  let t7;
  if ($[12] !== email || $[13] !== executeRecaptcha) {
    t7 = async () => {
      if (!email) {
        setErrorMessage("Please enter your email address first.");
        return;
      }
      setIsPending(true);
      setErrorMessage("");
      setSuccessMessage("");
      setCodeDigits(["", "", "", "", "", ""]);
      const recaptchaToken = await executeRecaptcha("password_reset");
      if (!recaptchaToken) {
        setErrorMessage("Security verification failed. Please try again.");
        setIsPending(false);
        return;
      }
      const response_0 = await sendPasswordResetCodeAction({
        email,
        recaptchaToken
      });
      setIsPending(false);
      if (response_0.success) {
        setResetEmail(email, response_0.maskedEmail);
        setMaskedEmailState(response_0.maskedEmail || email);
        setNeedsEmail(false);
        setSuccessMessage("A new code has been sent to your email.");
        setTimeout(() => setSuccessMessage(""), 5000);
        codeInputRefs.current[0]?.focus();
      } else {
        setErrorMessage(response_0.message || "Failed to send code. Please try again.");
      }
    };
    $[12] = email;
    $[13] = executeRecaptcha;
    $[14] = t7;
  } else {
    t7 = $[14];
  }
  const handleResendCode = t7;
  let t8;
  if ($[15] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = (index, value) => {
      const digit = value.replace(/\D/g, "").slice(-1);
      setCodeDigits(prev => {
        const newDigits = [...prev];
        newDigits[index] = digit;
        return newDigits;
      });
      if (digit && index < 5) {
        codeInputRefs.current[index + 1]?.focus();
      }
    };
    $[15] = t8;
  } else {
    t8 = $[15];
  }
  const handleDigitChange = t8;
  let t9;
  if ($[16] !== codeDigits) {
    t9 = (index_0, e_0) => {
      if (e_0.key === "Backspace" && !codeDigits[index_0] && index_0 > 0) {
        codeInputRefs.current[index_0 - 1]?.focus();
      }
    };
    $[16] = codeDigits;
    $[17] = t9;
  } else {
    t9 = $[17];
  }
  const handleDigitKeyDown = t9;
  let t10;
  if ($[18] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = e_1 => {
      e_1.preventDefault();
      const pastedData = e_1.clipboardData.getData("text").replace(/\D/g, "").slice(0, 6);
      const digits = pastedData.split("");
      setCodeDigits(prev_0 => {
        const newDigits_0 = [...prev_0];
        digits.forEach((digit_0, i) => {
          if (i < 6) {
            newDigits_0[i] = digit_0;
          }
        });
        return newDigits_0;
      });
      const focusIndex = Math.min(digits.length, 5);
      codeInputRefs.current[focusIndex]?.focus();
    };
    $[18] = t10;
  } else {
    t10 = $[18];
  }
  const handlePaste = t10;
  let t11;
  if ($[19] !== codeDigits) {
    t11 = codeDigits.every(_temp);
    $[19] = codeDigits;
    $[20] = t11;
  } else {
    t11 = $[20];
  }
  const isCodeComplete = t11;
  if (!isInitialized) {
    let t12;
    if ($[21] === Symbol.for("react.memo_cache_sentinel")) {
      t12 = <div className="flex justify-center py-8"><FontAwesomeIcon icon={faSpinnerThird} className="animate-spin text-2xl text-gray-400" /></div>;
      $[21] = t12;
    } else {
      t12 = $[21];
    }
    return t12;
  }
  let t12;
  if ($[22] !== email || $[23] !== isPending || $[24] !== maskedEmail || $[25] !== needsEmail) {
    t12 = needsEmail ? <><p className="text-sm text-gray-600 mb-4">Enter your email address and the 6-digit code we sent you.</p><div className="form-group mb-4"><input id="verify-email" type="email" name="email" value={email} onChange={e_2 => setEmail(e_2.target.value)} autoComplete="email" placeholder=" " required={true} className="form-input peer" disabled={isPending} /><label htmlFor="verify-email" className="form-input-label peer-placeholder-shown:form-input-label-placeholder-shown">Email <span className="text-primary">*</span></label></div></> : <p className="text-sm text-gray-600 mb-6 text-center">We sent a 6-digit code to <strong>{maskedEmail}</strong></p>;
    $[22] = email;
    $[23] = isPending;
    $[24] = maskedEmail;
    $[25] = needsEmail;
    $[26] = t12;
  } else {
    t12 = $[26];
  }
  let t13;
  if ($[27] !== errorMessage) {
    t13 = errorMessage && <div className="text-red text-sm mb-4 p-3 bg-red-50 rounded text-center">{errorMessage}</div>;
    $[27] = errorMessage;
    $[28] = t13;
  } else {
    t13 = $[28];
  }
  let t14;
  if ($[29] !== successMessage) {
    t14 = successMessage && <div className="text-green-700 text-sm mb-4 p-3 bg-green-50 rounded text-center">{successMessage}</div>;
    $[29] = successMessage;
    $[30] = t14;
  } else {
    t14 = $[30];
  }
  let t15;
  if ($[31] !== codeDigits || $[32] !== handleDigitKeyDown || $[33] !== isPending) {
    t15 = codeDigits.map((digit_1, index_1) => <input key={index_1} ref={el => codeInputRefs.current[index_1] = el} type="text" inputMode="numeric" pattern="[0-9]*" value={digit_1} onChange={e_3 => handleDigitChange(index_1, e_3.target.value)} onKeyDown={e_4 => handleDigitKeyDown(index_1, e_4)} onPaste={handlePaste} autoComplete={index_1 === 0 ? "one-time-code" : "off"} maxLength={1} disabled={isPending} className="w-12 h-14 text-center text-xl font-medium border border-gray-300 rounded-md focus:border-primary focus:ring-1 focus:ring-primary outline-none transition-colors disabled:bg-gray-100" aria-label={`Digit ${index_1 + 1}`} />);
    $[31] = codeDigits;
    $[32] = handleDigitKeyDown;
    $[33] = isPending;
    $[34] = t15;
  } else {
    t15 = $[34];
  }
  let t16;
  if ($[35] !== t15) {
    t16 = <div className="flex justify-center gap-2 mb-6">{t15}</div>;
    $[35] = t15;
    $[36] = t16;
  } else {
    t16 = $[36];
  }
  const t17 = isPending || !isCodeComplete || !email;
  const t18 = `w-full btn btn-primary flex items-center justify-center mb-4 ${isPending || !isCodeComplete ? "opacity-75" : ""}`;
  let t19;
  if ($[37] !== isPending) {
    t19 = isPending ? <><FontAwesomeIcon icon={faSpinnerThird} className="animate-spin mr-2" />Verifying...</> : "Continue";
    $[37] = isPending;
    $[38] = t19;
  } else {
    t19 = $[38];
  }
  let t20;
  if ($[39] !== t17 || $[40] !== t18 || $[41] !== t19) {
    t20 = <button type="submit" disabled={t17} className={t18}>{t19}</button>;
    $[39] = t17;
    $[40] = t18;
    $[41] = t19;
    $[42] = t20;
  } else {
    t20 = $[42];
  }
  let t21;
  if ($[43] !== handleResendCode || $[44] !== isPending) {
    t21 = <p className="text-xs text-gray-500 text-center mb-4">Didn't receive the code?{" "}<button type="button" onClick={handleResendCode} disabled={isPending} className="text-primary hover:underline disabled:opacity-50">Resend</button></p>;
    $[43] = handleResendCode;
    $[44] = isPending;
    $[45] = t21;
  } else {
    t21 = $[45];
  }
  let t22;
  if ($[46] === Symbol.for("react.memo_cache_sentinel")) {
    t22 = <Link href="/account/reset-password" className="hover:text-primary transition-colors">Start over</Link>;
    $[46] = t22;
  } else {
    t22 = $[46];
  }
  let t23;
  if ($[47] === Symbol.for("react.memo_cache_sentinel")) {
    t23 = <p className="text-xs text-gray-400 text-center pt-4 border-t border-gray-200">{t22}{" \xB7 "}<Link href="/account/login" className="hover:text-primary transition-colors">Back to sign in</Link></p>;
    $[47] = t23;
  } else {
    t23 = $[47];
  }
  let t24;
  if ($[48] !== handleCodeSubmit || $[49] !== t12 || $[50] !== t13 || $[51] !== t14 || $[52] !== t16 || $[53] !== t20 || $[54] !== t21) {
    t24 = <form onSubmit={handleCodeSubmit}>{t12}{t13}{t14}{t16}{t20}{t21}{t23}</form>;
    $[48] = handleCodeSubmit;
    $[49] = t12;
    $[50] = t13;
    $[51] = t14;
    $[52] = t16;
    $[53] = t20;
    $[54] = t21;
    $[55] = t24;
  } else {
    t24 = $[55];
  }
  return t24;
};
export default CodeStep;
function _temp(d) {
  return d !== "";
}